import type { Dispatch, SetStateAction } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Typography } from '@interstate/components/Typography';
import { Alert } from '@interstate/components/Alert';

import * as dealScreenErrorSelectors from '@makemydeal/dr-dash-store/selectors/dealScreenErrorSelectors';
import * as incentivesActionCreators from '@makemydeal/dr-dash-store/actions/incentivesActionCreators';
import * as programActionCreators from '@makemydeal/dr-dash-store/actions/programActionCreators';
import type { ManualIncentiveEditEntry } from '@makemydeal/dr-dash-store/types/manualIncentiveTypes';

import type { StateTree } from '@makemydeal/dr-dash-types';
import { INCENTIVE_TYPE_VALUES } from '@makemydeal/dr-dash-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import { ManualRebatesFormContainer } from './Rebates.style';
import { ManualRebatesFormHeader } from './ManualRebatesFormHeader';
import { RebatesTableRow } from './RebatesTableRow';
import type { FieldValidationChangeParams } from '../../types';
import { INCENTIVE_FIELD_NAMES, INCENTIVE_MODIFICATION_ACTION, INCENTIVES, PAYMENT_FIELDS } from '../../constants';
import {
    isExemptFromCodeUniqueness,
    getRequiredFields,
    getUpdatedValidationErrors,
    getValidationErrorMessage,
    hasAnyErrors,
    isConversionToCustomIncentive,
    isIncentiveFromProgram,
    isProgramIncentiveOverride,
    resolveManualFlag,
    validateRequiredFields,
    validateAllRequiredFields,
    revalidateAllIncentives
} from '../../utils/rebatesUtils';

export interface ManualRebatesForm {
    incentivesToEdit: ManualIncentiveEditEntry[];
    term: number | undefined;
    editedIncentiveIndex: number;
    setEditedIncentiveIndex: (value: number) => void;
    setPaymentUpdateEligibility: (shouldUpdate: boolean) => void;
    setIncentivesToEdit: Dispatch<SetStateAction<ManualIncentiveEditEntry[]>>;
    onIncentiveChange: (incentive: ManualIncentiveEditEntry, action: 'add' | 'remove' | 'edit') => void;
    showCashProgramLabel?: boolean;
}

export const ManualRebatesForm = memo(function ManualRebatesForm({
    incentivesToEdit,
    term,
    onIncentiveChange,
    editedIncentiveIndex,
    setEditedIncentiveIndex,
    setPaymentUpdateEligibility,
    setIncentivesToEdit,
    showCashProgramLabel = false
}: ManualRebatesForm) {
    const dispatch = useDispatch();
    const codeCellErrorIndexesRef = useRef<Set<number>>(new Set());
    const codeCellErrorIndexes = codeCellErrorIndexesRef.current;
    const incentivesToEditRef = useRef(incentivesToEdit);
    const isOptionalDeskingMode = useSelector(dealModeSelectors.isOptionalDeskingMode);
    const isRestorationEnabled = useSelector(featureToggleSelectors.isProgramIncentivesRestorationEnabled);
    const isInitialMountRef = useRef(true);

    const incentivesErrors = useSelector((state: StateTree) =>
        dealScreenErrorSelectors.getCategoryValidationErrors(state, DealTabs.INCENTIVES, INCENTIVES)
    );

    const incentivesErrorsRef = useRef(incentivesErrors);
    incentivesErrorsRef.current = incentivesErrors;
    incentivesToEditRef.current = incentivesToEdit;

    const [validationErrors, setValidationErrors] = useState<
        Record<number, Partial<Record<keyof ManualIncentiveEditEntry, boolean>>>
    >({});

    const getIsUniqueCode = useCallback(
        (currentIndex: number, currentCode: number | undefined, incentivesList: ManualIncentiveEditEntry[] = incentivesToEdit) =>
            !incentivesList.find(
                (incentive, index) =>
                    index !== currentIndex &&
                    incentive.code &&
                    incentive.code == currentCode &&
                    !incentive.deleted &&
                    !isExemptFromCodeUniqueness(incentivesList[currentIndex], incentive, isRestorationEnabled)
            ),
        [incentivesToEdit, isRestorationEnabled]
    );

    const updateIncentiveField = useCallback(
        (currentIncentive: ManualIncentiveEditEntry, fieldName: keyof ManualIncentiveEditEntry, textValue: string | number) => {
            const manual = resolveManualFlag(currentIncentive, fieldName, textValue);
            const updated = { ...currentIncentive, [fieldName]: textValue, manual };
            if (
                isRestorationEnabled &&
                fieldName !== INCENTIVE_FIELD_NAMES.TYPE &&
                isProgramIncentiveOverride(updated) &&
                updated.type !== INCENTIVE_TYPE_VALUES.dealerCash.value
            ) {
                updated.type = INCENTIVE_TYPE_VALUES.customerCash.value;
            }
            return updated;
        },
        [isRestorationEnabled]
    );

    const getReduxAction = useCallback(
        (currentIncentive: ManualIncentiveEditEntry, updated: ManualIncentiveEditEntry) => {
            const requiredFields = getRequiredFields(isOptionalDeskingMode);
            const hasRequiredFields = requiredFields.every((field) => updated[field]);

            if (!hasRequiredFields) {
                return null;
            }

            if (currentIncentive.isNewIncentive) {
                return { incentive: { ...updated, isNewIncentive: false }, action: 'add' as const };
            }

            return { incentive: updated, action: 'edit' as const };
        },
        [isOptionalDeskingMode]
    );

    const shouldDispatchToRedux = useCallback(
        (incentive: ManualIncentiveEditEntry, incentiveIndex: number) => {
            if (!incentive.code) {
                return true;
            }
            return getIsUniqueCode(incentiveIndex, Number(incentive.code));
        },
        [getIsUniqueCode]
    );

    const deselectProgramIncentiveOnConversion = useCallback(
        (previous: ManualIncentiveEditEntry, updated: ManualIncentiveEditEntry) => {
            if (!isRestorationEnabled || !isConversionToCustomIncentive(previous, updated)) {
                return;
            }
            dispatch(programActionCreators.deselectIncentiveForConversion({ incentiveId: Number(previous.code) }));
        },
        [dispatch, isRestorationEnabled]
    );

    const onInputChange = useCallback(
        (textValue: string | number, fieldName: keyof ManualIncentiveEditEntry, incentiveIndex: number) => {
            setEditedIncentiveIndex(incentiveIndex);

            const currentIncentive = incentivesToEdit[incentiveIndex];
            const updated = updateIncentiveField(currentIncentive, fieldName, textValue);

            const shouldDispatch = shouldDispatchToRedux(updated, incentiveIndex);

            let finalUpdate = updated;

            if (shouldDispatch) {
                const reduxAction = getReduxAction(currentIncentive, updated);

                if (reduxAction) {
                    finalUpdate = reduxAction.incentive;
                    const isPaymentField = PAYMENT_FIELDS.includes(fieldName);
                    setPaymentUpdateEligibility(isPaymentField);
                    deselectProgramIncentiveOnConversion(currentIncentive, updated);
                    onIncentiveChange(reduxAction.incentive, reduxAction.action);
                }
            }

            setIncentivesToEdit((incentives) =>
                incentives.map((incentive, index) => (index === incentiveIndex ? finalUpdate : incentive))
            );
        },
        [
            setEditedIncentiveIndex,
            incentivesToEdit,
            updateIncentiveField,
            getReduxAction,
            shouldDispatchToRedux,
            setIncentivesToEdit,
            setPaymentUpdateEligibility,
            deselectProgramIncentiveOnConversion,
            onIncentiveChange
        ]
    );

    const handleIncentiveDeleteOrUndo = useCallback(
        (index: number) => {
            setEditedIncentiveIndex(index);

            const currentIncentive = incentivesToEditRef.current[index];

            if (isRestorationEnabled && !currentIncentive.deleted && isProgramIncentiveOverride(currentIncentive)) {
                const deletedOverride = { ...currentIncentive, deleted: true };
                setIncentivesToEdit((incentives) => incentives.map((inc, idx) => (idx === index ? deletedOverride : inc)));
                setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
                setPaymentUpdateEligibility(true);
                onIncentiveChange(currentIncentive, INCENTIVE_MODIFICATION_ACTION.REMOVE);
                return;
            }

            const undoingProgramOverride =
                isRestorationEnabled && currentIncentive.deleted && isProgramIncentiveOverride(currentIncentive);
            const undoingProgramConditional =
                isRestorationEnabled &&
                currentIncentive.deleted &&
                isIncentiveFromProgram(currentIncentive) &&
                !isProgramIncentiveOverride(currentIncentive);
            const updated = {
                ...currentIncentive,
                deleted: !currentIncentive.deleted,
                ...(undoingProgramOverride ? { manual: true } : {}),
                ...(undoingProgramConditional ? { manual: false } : {})
            };

            setIncentivesToEdit((incentives) => incentives.map((inc, idx) => (idx === index ? updated : inc)));

            const updatedCode = updated.code !== undefined && updated.code !== null ? Number(updated.code) : undefined;
            const hasDuplicate = updatedCode !== undefined ? !getIsUniqueCode(index, updatedCode) : false;

            if (updated.deleted) {
                setValidationErrors((prev) => {
                    const next = { ...prev };
                    delete next[index];
                    return next;
                });
            } else {
                setValidationErrors((prev) => {
                    const rowErrors = validateAllRequiredFields(updated, isOptionalDeskingMode);
                    return getUpdatedValidationErrors(prev, index, rowErrors);
                });
            }

            if (!hasDuplicate) {
                const action = updated.deleted ? INCENTIVE_MODIFICATION_ACTION.REMOVE : INCENTIVE_MODIFICATION_ACTION.ADD;
                setPaymentUpdateEligibility(true);
                onIncentiveChange(updated, action);
            }
        },
        [
            setEditedIncentiveIndex,
            setIncentivesToEdit,
            getIsUniqueCode,
            setPaymentUpdateEligibility,
            onIncentiveChange,
            isOptionalDeskingMode,
            isRestorationEnabled
        ]
    );

    const handleFieldValidationChange = useCallback(
        ({ incentiveIndex, fieldName, hasError }: FieldValidationChangeParams) => {
            const incentive = incentivesToEditRef.current[incentiveIndex];

            setValidationErrors((prevValidation) => {
                const rowErrors = validateRequiredFields(incentive, fieldName, hasError, isOptionalDeskingMode);
                const updated = getUpdatedValidationErrors(prevValidation, incentiveIndex, rowErrors);
                if (!isOptionalDeskingMode) {
                    return revalidateAllIncentives(updated, incentivesToEditRef.current, isOptionalDeskingMode, incentiveIndex);
                }
                return updated;
            });
        },
        [isOptionalDeskingMode]
    );

    useEffect(() => {
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }

        setValidationErrors((prevValidation) => revalidateAllIncentives(prevValidation, incentivesToEdit, isOptionalDeskingMode));
    }, [incentivesToEdit.length, isOptionalDeskingMode]);

    const errorMessage = getValidationErrorMessage(validationErrors);

    useEffect(() => {
        const hasValidationErrors = Object.values(validationErrors).some(hasAnyErrors);

        dispatch(incentivesActionCreators.setIncentiveValidationError(errorMessage, hasValidationErrors));
    }, [validationErrors, dispatch]);

    const showValidationError = Object.values(validationErrors).some(hasAnyErrors);

    return (
        <ManualRebatesFormContainer id="manual-rebates-form-container">
            {showValidationError && (
                <Alert
                    data-testid="manual-incentives-validation-error"
                    role="manual-incentives-validation-error"
                    type="error"
                    sx={{ marginBottom: '1rem' }}
                >
                    <Typography tag="span" variant="body-sm" data-testid="manual-incentives-validation-error-message">
                        {errorMessage}
                    </Typography>
                </Alert>
            )}
            <ManualRebatesFormHeader />
            {incentivesToEdit.map((incentive, index: number) => (
                <RebatesTableRow
                    key={`incentive-row-${index}`}
                    term={term}
                    incentive={incentive}
                    incentiveIndex={index}
                    isEditing={index === editedIncentiveIndex}
                    codeCellErrorIndexes={codeCellErrorIndexes}
                    handleIncentiveDeleteOrUndo={handleIncentiveDeleteOrUndo}
                    isUniqueCode={getIsUniqueCode(index, incentive.code)}
                    handleInputChange={onInputChange}
                    onFieldValidationChange={handleFieldValidationChange}
                    showCashProgramLabel={showCashProgramLabel}
                />
            ))}
        </ManualRebatesFormContainer>
    );
});
